/**
 * Adding a passkey, in two halves.
 *
 * 1. `prepareRegistration`, before the click: ask the server for the options
 *    and the signed token (`POST /members/passkeys/registration`). This is also
 *    where the server's fresh-sign-in gate answers `reauth_required`.
 * 2. `completeEnrolment`, inside the click: have the browser make the passkey,
 *    then save it under its label.
 *
 * The split is for Safari. WebKit only lets `navigator.credentials.create` run
 * with a recent click, and a network round trip between the click and the
 * call can use that up, after which the browser refuses with NotAllowedError.
 * `completeEnrolment` calls `create` before it first awaits, so a click handler
 * that calls it directly keeps the click.
 *
 * The server's token lives two minutes from mint (X.StepUp.Enrolment
 * `@ttl_seconds`, sent as `timeoutMs`). The browser prompt is given only what
 * is left of that, less time for the save to reach the server
 * (`promptTimeoutMs`); otherwise a slow enrolment (a phone over QR, say) could
 * finish after the token died, be refused, and leave the authenticator holding
 * a passkey Ploy never saved. Options too old to leave a usable prompt
 * (`isFresh`) are fetched again before the click is used.
 *
 * The server and the browser are passed in, so every branch is unit tested.
 */
import {
  classifyCeremonyError,
  toCreationOptions,
  type AttestationFields,
} from "modules/members/webauthn";
import type { MFPasskey, PasskeyRegistration } from "state/members/passkeys";
import {
  failureMessage,
  isReauthRequired,
  SAVE_PASSKEY_FAILED_MESSAGE,
  START_REGISTRATION_FAILED_MESSAGE,
} from "state/members/stepUpFailure";

/** Options this old are refreshed in the background: half the token's two minutes. */
export const REGISTRATION_MAX_AGE_MS = 60_000;
/** Kept back from the prompt so the save still lands inside the token's life. */
export const SAVE_MARGIN_MS = 10_000;
/** The least prompt time worth offering; with less, fetch fresh options first. */
export const MIN_PROMPT_MS = 45_000;
/** Never ask the browser for a prompt shorter than this, whatever the server sent. */
export const PROMPT_FLOOR_MS = 10_000;

export type PreparedRegistration = {
  registration: PasskeyRegistration;
  fetchedAt: number;
};

export type PrepareOutcome =
  | { kind: "ready"; prepared: PreparedRegistration }
  | { kind: "reauth" }
  | { kind: "failed"; message: string };

export type EnrolOutcome =
  | { kind: "created"; passkey: MFPasskey }
  | { kind: "reauth" }
  /** Aborted. Nothing was made and there is nothing to say. */
  | { kind: "cancelled" }
  | { kind: "failed"; message: string };

export type EnrolDeps = {
  create: (
    options: PublicKeyCredentialCreationOptions,
  ) => Promise<AttestationFields>;
  save: (
    challengeToken: string,
    attestation: AttestationFields,
    label: string,
  ) => Promise<MFPasskey>;
};

export const PROMPT_CLOSED_MESSAGE =
  "The passkey prompt closed before a passkey was made. Try again.";
export const ALREADY_REGISTERED_MESSAGE =
  "This device already has a passkey for your account. Use that one, or add a passkey on another device.";
export const CREATE_FAILED_MESSAGE =
  "Your browser could not make a passkey here. Try again, or try another browser.";

/**
 * How long the browser prompt may stay open: what is left of the token's life
 * (`fetchedAt + timeoutMs`), less the save margin. May be zero or negative for
 * old options; `isFresh` keeps those from being used.
 */
export const promptTimeoutMs = (
  prepared: PreparedRegistration,
  now: number,
): number =>
  prepared.fetchedAt +
  prepared.registration.publicKey.timeoutMs -
  SAVE_MARGIN_MS -
  now;

export const isFresh = (
  prepared: PreparedRegistration | null,
  now: number,
): prepared is PreparedRegistration =>
  prepared !== null &&
  now >= prepared.fetchedAt &&
  now - prepared.fetchedAt < REGISTRATION_MAX_AGE_MS &&
  promptTimeoutMs(prepared, now) >= MIN_PROMPT_MS;

export const prepareRegistration = async (
  start: () => Promise<PasskeyRegistration>,
  now: () => number = Date.now,
): Promise<PrepareOutcome> => {
  try {
    const registration = await start();
    return { kind: "ready", prepared: { registration, fetchedAt: now() } };
  } catch (error) {
    if (isReauthRequired(error)) return { kind: "reauth" };
    return {
      kind: "failed",
      message: failureMessage(error, START_REGISTRATION_FAILED_MESSAGE),
    };
  }
};

const browserProblem = (error: unknown): EnrolOutcome => {
  const problem = classifyCeremonyError(error);
  if (problem === "cancelled") return { kind: "cancelled" };
  return {
    kind: "failed",
    message:
      problem === "not_allowed"
        ? PROMPT_CLOSED_MESSAGE
        : problem === "already_registered"
          ? ALREADY_REGISTERED_MESSAGE
          : CREATE_FAILED_MESSAGE,
  };
};

/**
 * Makes and saves the passkey. `deps.create` runs before the first await, so
 * call this straight from the click handler. The prompt gets what is left of
 * the token's life (`promptTimeoutMs`), never less than `PROMPT_FLOOR_MS`.
 */
export const completeEnrolment = async (
  prepared: PreparedRegistration,
  label: string,
  deps: EnrolDeps,
  now: number = Date.now(),
): Promise<EnrolOutcome> => {
  const { registration } = prepared;
  let attestation: AttestationFields;
  try {
    const timeoutMs = Math.max(PROMPT_FLOOR_MS, promptTimeoutMs(prepared, now));
    attestation = await deps.create(
      toCreationOptions(registration.publicKey, timeoutMs),
    );
  } catch (error) {
    return browserProblem(error);
  }

  try {
    const passkey = await deps.save(registration.challengeToken, attestation, label);
    return { kind: "created", passkey };
  } catch (error) {
    if (isReauthRequired(error)) return { kind: "reauth" };
    return {
      kind: "failed",
      message: failureMessage(error, SAVE_PASSKEY_FAILED_MESSAGE),
    };
  }
};
